// Pure fleet-deployment adapter. No React, no fetch. Types match the
// backend's GET /deployments response.

export const FLEET_COMPONENTS_ANNOTATION = 'everdriven.com/fleet-components';
export const FLEET_SHAPE_ANNOTATION = 'everdriven.com/fleet-shape';

/** Promotion targets. `dev` is deliberately absent: it is continuous
 * deployment owned by argocd-image-updater, and anything written there is
 * overwritten on the next poll. Nothing in these screens may offer it. */
// `mgmt` is the cluster the platform's own tooling runs on, and for those apps
// it is production — there is no staging in front of it. Only two apps have an
// mgmt overlay, so this widens the vocabulary rather than the fleet.
/**
 * What is known about whether a commit has an image to promote.
 *
 * `known: false` is not a failure — it means the question could not be
 * answered for this repository, and a release is allowed through rather than
 * blocked on ignorance.
 *
 * The two sides that ask this — the scaffolder action, which must be
 * authoritative at push time, and the backend that feeds the dialog — each do
 * their own request with their own credentials. What they must not disagree on
 * is what counts as a build and what the answer means, so that lives here.
 */
export type BuildVerdict =
  | { known: false; reason: string }
  | { known: true; built: true }
  | { known: true; built: false; lastBuiltSha: string | null };

/**
 * The workflow that produces the image, as the fleet actually names it:
 * fourteen repositories use `build.yaml`, `catalyst-trip-hq` uses
 * `build-image.yaml`, and `developer-portal` uses `build.yml`.
 */
export const BUILD_WORKFLOW = /(^|\/)build(-image)?\.ya?ml$/;

/**
 * Components whose repository is not named after them.
 *
 * `catalyst-routing` and `catalyst-pricing` are two apps built from one
 * repository, so a release of either is a tag on `catalyst-routing-pricing` —
 * and a question about either is a question about that repository. Shared
 * because both sides ask: the action to know what to tag, the backend to know
 * what to look for a build in. They cannot be allowed to differ.
 */
export const DEFAULT_REPOSITORIES: Record<string, string> = {
  'catalyst-routing': 'catalyst-routing-pricing',
  'catalyst-pricing': 'catalyst-routing-pricing',
};

export function repositoryFor(
  service: string,
  overrides: Record<string, string> = {},
): string {
  return overrides[service] ?? DEFAULT_REPOSITORIES[service] ?? service;
}

export const PROMOTABLE_ENVIRONMENTS = ['staging', 'prod', 'mgmt'] as const;

/**
 * How an environment is written on screen.
 *
 * These are identifiers from the overlay paths, not proper nouns, so they stay
 * lower case — `staging`, `mgmt`. `prod` is spelled out because the short form
 * reads as an abbreviation of the word rather than the name of a place.
 *
 * Shared because three surfaces render it and each had grown its own rule: a
 * radio offering "staging" beside "Production", a title saying "to
 * production", and a column header disagreeing with both.
 */
export function environmentLabel(environment: string): string {
  return environment === 'prod' ? 'production' : environment;
}

export type PromotableEnvironment = (typeof PROMOTABLE_ENVIRONMENTS)[number];

export interface DeployedUnit {
  environment: string;
  /** The `values.yaml` key that was written — `api` or `ep`. */
  component: string;
  /** Full overlay value, e.g. `api-1.0.2-rc.2`, or `web-1.1.5` on trip-hq. */
  imageTag: string;
}

export interface ServiceDeployments {
  service: string;
  /** `special` marks entities with no deployable component — the API gateway
   * and the seed orchestrator. They are in the catalog but never promoted. */
  shape: 'standard' | 'special';
  /** True when the service rebuilds per environment instead of retagging, so
   * its production image is never the artifact staging validated. trip-hq is
   * the only one today. */
  rebuildsPerEnvironment: boolean;
  units: DeployedUnit[];
}

export interface FleetDeploymentsResponse {
  services: ServiceDeployments[];
}

/** How a version string is shaped. The three forms are enforced by different
 * tooling, so a tag that does not parse is a signal, not a rounding error. */
export type VersionKind = 'release' | 'releaseCandidate' | 'commit' | 'unknown';

export interface ParsedVersion {
  /** The build target the tag is prefixed with — `api`, `ep`, `web`. Not the
   * same thing as the values key it is stored under: trip-hq keeps a `web-`
   * tag in `api.imageTag`. */
  prefix: string;
  /** Everything after the prefix: `1.0.2`, `1.0.2-rc.2`, `6c73827`. */
  version: string;
  kind: VersionKind;
}

// Three conventions are in use across the fleet's repositories: plain
// `0.1.2-rc.3`, `v`-prefixed `v0.12.1-rc.1`, and `v1.0.0-rc3` with no dot
// before the candidate. All three are read, and a version is written back in
// the style of the tag it was derived from — emitting one repository's
// convention into another would leave it carrying two.
const RELEASE = /^v?(\d+)\.(\d+)\.(\d+)$/;
const RELEASE_CANDIDATE = /^v?(\d+)\.(\d+)\.(\d+)-rc\.?(\d+)$/;
const COMMIT = /^[0-9a-f]{7,12}$/;

export function parseImageTag(imageTag: string): ParsedVersion | null {
  const separator = imageTag.indexOf('-');
  if (separator <= 0) {
    return null;
  }
  const prefix = imageTag.slice(0, separator);
  const version = imageTag.slice(separator + 1);

  let kind: VersionKind = 'unknown';
  if (RELEASE.test(version)) {
    kind = 'release';
  } else if (RELEASE_CANDIDATE.test(version)) {
    kind = 'releaseCandidate';
  } else if (COMMIT.test(version)) {
    kind = 'commit';
  }

  return { prefix, version, kind };
}

/**
 * Semver precedence, narrowed to the two forms this fleet uses.
 *
 * Returns a negative number when `a` sorts before `b`, positive when after,
 * zero when equal. Versions that do not parse compare as equal to everything,
 * so an unrecognised tag never claims to be ahead of a real one.
 *
 * The rule that matters: a release candidate sorts *below* its own release,
 * so `0.1.2-rc.3` is older than `0.1.2`, not newer.
 */
export function compareVersions(a: string, b: string): number {
  const left = numericParts(a);
  const right = numericParts(b);
  if (!left || !right) {
    return 0;
  }

  for (let i = 0; i < 3; i++) {
    if (left.core[i] !== right.core[i]) {
      return left.core[i] - right.core[i];
    }
  }

  // Same x.y.z. A release (no candidate number) outranks any candidate of it.
  if (left.candidate === null && right.candidate === null) return 0;
  if (left.candidate === null) return 1;
  if (right.candidate === null) return -1;
  return left.candidate - right.candidate;
}

/** How a repository writes its tags, so a derived version can match. */
export interface TagStyle {
  /** `v0.12.1` rather than `0.12.1`. */
  prefix: 'v' | '';
  /** `-rc.1` rather than `-rc1`. */
  rcSeparator: '.' | '';
}

/**
 * The style a repository writes tags in.
 *
 * The prefix is read from the tag being continued, but the candidate separator
 * has to come from the whole list: the highest tag is often a release, which
 * carries no candidate and so cannot say whether this repository writes `-rc.1`
 * or `-rc1`. A dot is the default because that is what all but one repository
 * uses; the dotless form is only assumed when a tag actually shows it.
 */
export function tagStyleOf(tag: string, tags: string[] = [tag]): TagStyle {
  return {
    prefix: tag.startsWith('v') ? 'v' : '',
    rcSeparator: tags.some(t => /-rc\d+$/.test(t)) ? '' : '.',
  };
}

export function formatVersion(
  core: [number, number, number],
  candidate: number | null,
  style: TagStyle,
): string {
  const version = `${style.prefix}${core[0]}.${core[1]}.${core[2]}`;
  return candidate === null
    ? version
    : `${version}-rc${style.rcSeparator}${candidate}`;
}

function numericParts(
  version: string,
): { core: [number, number, number]; candidate: number | null } | null {
  const release = RELEASE.exec(version);
  if (release) {
    return {
      core: [Number(release[1]), Number(release[2]), Number(release[3])],
      candidate: null,
    };
  }
  const candidate = RELEASE_CANDIDATE.exec(version);
  if (candidate) {
    return {
      core: [Number(candidate[1]), Number(candidate[2]), Number(candidate[3])],
      candidate: Number(candidate[4]),
    };
  }
  return null;
}

export interface PendingRelease {
  /** The staging version that production does not have yet. */
  version: string;
  component: string;
}

/**
 * What staging is holding that production has not taken.
 *
 * This is the question the fleet card exists to answer, and the one nobody can
 * answer today without opening the repo. Note it is *not* "how far behind is
 * prod": most services sit at `0.1.2-rc.N` in staging and `0.1.2` in
 * production, which means production is ahead and nothing is waiting.
 *
 * Returns one entry per component whose staging version outranks production's.
 */
export function pendingReleases(service: ServiceDeployments): PendingRelease[] {
  const pending: PendingRelease[] = [];

  for (const component of componentsOf(service)) {
    const staging = unitFor(service, 'staging', component);
    const production = unitFor(service, 'prod', component);
    if (!staging || !production) {
      continue;
    }

    const stagingVersion = parseImageTag(staging.imageTag);
    const productionVersion = parseImageTag(production.imageTag);
    if (!stagingVersion || !productionVersion) {
      continue;
    }

    if (compareVersions(stagingVersion.version, productionVersion.version) > 0) {
      pending.push({ version: stagingVersion.version, component });
    }
  }

  return pending;
}

export function componentsOf(service: ServiceDeployments): string[] {
  const seen = new Set<string>();
  for (const unit of service.units) {
    seen.add(unit.component);
  }
  return [...seen].sort();
}

export function unitFor(
  service: ServiceDeployments,
  environment: string,
  component: string,
): DeployedUnit | undefined {
  return service.units.find(
    unit => unit.environment === environment && unit.component === component,
  );
}

/** Units the promote screens may act on: staging and production only. */
export function promotableUnits(service: ServiceDeployments): DeployedUnit[] {
  return service.units.filter(unit =>
    (PROMOTABLE_ENVIRONMENTS as readonly string[]).includes(unit.environment),
  );
}

/**
 * The components a System's fleet view should list.
 *
 * The backend reports overlay facts for everything in gitops-platform, legacy
 * services included, because the overlay is the authority on what is deployed.
 * Which of those belong to a system is a catalog question, answered here from
 * the System's own relations — that split is the rule the contract sets, and
 * skipping it lists the whole repository instead of the fleet.
 *
 * `members` is the set of component names; passing undefined lists everything,
 * which is only right for a view that is not scoped to a system.
 */
export function promotableServices(
  response: FleetDeploymentsResponse,
  members?: Set<string>,
): ServiceDeployments[] {
  return response.services
    .filter(service => !members || members.has(service.service))
    .filter(service => service.shape !== 'special')
    .filter(service => promotableUnits(service).length > 0)
    .sort((a, b) => a.service.localeCompare(b.service));
}

/**
 * Component names a System has as parts, from its catalog relations.
 *
 * Relations are entity refs — `component:default/catalyst-assignment` — so the
 * name is the segment after the last slash.
 */
export function systemMembers(
  relations: Array<{ type: string; targetRef: string }> | undefined,
): Set<string> {
  const members = new Set<string>();
  for (const relation of relations ?? []) {
    if (relation.type !== 'hasPart') {
      continue;
    }
    const [kind] = relation.targetRef.split(':');
    if (kind !== 'component') {
      continue;
    }
    const name = relation.targetRef.split('/').pop();
    if (name) {
      members.add(name);
    }
  }
  return members;
}

export type Bump = 'patch' | 'minor' | 'major';

function bumped(
  core: [number, number, number],
  bump: Bump,
): [number, number, number] {
  const [major, minor, patch] = core;
  if (bump === 'major') {
    return [major + 1, 0, 0];
  }
  if (bump === 'minor') {
    return [major, minor + 1, 0];
  }
  return [major, minor, patch + 1];
}

const RELEASE_BRANCH = /^release\/(\d+\.\d+\.\d+)$/;

/**
 * A Git Flow release line in flight: `release/X.Y.Z` has been cut from `dev`,
 * and `X.Y.Z` has not been tagged yet — that tag is `release-finish`'s, on the
 * merge into `main`.
 */
export interface ReleaseLine {
  /** `X.Y.Z`, as `release-cut` wrote it — never `v`-prefixed. */
  version: string;
  branch: string;
  /** The line's own candidates, oldest first. */
  candidates: string[];
  /** The branch head is the commit its last candidate was cut from, so
   * another candidate would ship the same image again. Set by the backend,
   * which can see commits. */
  unchanged?: boolean;
  /** The line's pull request into `main`, while it is open. */
  pullRequest?: { number: number; url: string } | null;
}

/**
 * A gitops pull request that will move one environment of an app when it
 * merges — what tag-promotion opens after a candidate or a release is tagged.
 * Until it merges, the overlay still holds the old version, so without this
 * the screen shows nothing of a release that is one approval away.
 */
export interface PendingPromotion {
  environment: string;
  /** The tag the overlay moves to, e.g. `api-0.1.3-rc.1`. */
  imageTag: string;
  number: number;
  url: string;
}

/** What the release screen reads about one repository. */
export interface ReleaseState {
  /** Every tag, newest first. */
  tags: string[];
  line: ReleaseLine | null;
  pending: PendingPromotion[];
}

/**
 * Pending promotions grouped for display: one entry per environment and
 * version, with every pull request that carries it — tag-promotion opens one
 * per workload, so `api` and `ep` arrive as two pull requests for one move.
 */
export function pendingByEnvironment(
  pending: PendingPromotion[],
): Array<{ environment: string; version: string; pulls: Array<{ number: number; url: string }> }> {
  const grouped = new Map<
    string,
    { environment: string; version: string; pulls: Array<{ number: number; url: string }> }
  >();
  for (const entry of pending) {
    const version = parseImageTag(entry.imageTag)?.version ?? entry.imageTag;
    const key = `${entry.environment} ${version}`;
    const group = grouped.get(key) ?? { environment: entry.environment, version, pulls: [] };
    if (!group.pulls.some(pull => pull.number === entry.number)) {
      group.pulls.push({ number: entry.number, url: entry.url });
    }
    grouped.set(key, group);
  }
  return [...grouped.values()].map(group => ({
    ...group,
    pulls: group.pulls.sort((a, b) => a.number - b.number),
  }));
}

/**
 * The release line in flight, if any.
 *
 * Read from the branches, not the tags: under Git Flow a line opens by cutting
 * a branch, and the highest tag in the repository says nothing about which
 * line a candidate belongs to. A branch whose release is already tagged has
 * shipped, and one below the last release is a leftover nobody deleted —
 * neither is open.
 */
export function openReleaseLine(
  branches: string[],
  tags: string[],
): ReleaseLine | null {
  const releases = tags
    .map(numericParts)
    .filter((parts): parts is NonNullable<typeof parts> => Boolean(parts))
    .filter(parts => parts.candidate === null);
  const lastRelease = releases.reduce<(typeof releases)[number] | null>(
    (best, current) =>
      !best || compareParts(current, best) > 0 ? current : best,
    null,
  );

  const open = branches
    .map(branch => RELEASE_BRANCH.exec(branch))
    .filter((match): match is RegExpExecArray => Boolean(match))
    .map(match => ({ branch: match[0], parts: numericParts(match[1])! }))
    .filter(({ parts }) => !lastRelease || compareParts(parts, lastRelease) > 0)
    .sort((a, b) => compareParts(b.parts, a.parts));
  if (open.length === 0) {
    return null;
  }

  const { branch, parts } = open[0];
  const candidates = tags
    .map(tag => ({ tag, parts: numericParts(tag) }))
    .filter(
      ({ parts: candidate }) =>
        candidate !== null && candidate.candidate !== null &&
        candidate.core.every((n, i) => n === parts.core[i]),
    )
    .sort((a, b) => compareParts(a.parts!, b.parts!))
    .map(({ tag }) => tag);

  return { version: parts.core.join('.'), branch, candidates };
}

/** The candidate after the line's last one, in the style it was written in. */
export function nextCandidate(line: ReleaseLine): string {
  const last = line.candidates[line.candidates.length - 1];
  const parts = last ? numericParts(last) : null;
  const style: TagStyle = last
    ? tagStyleOf(last, line.candidates)
    : { prefix: '', rcSeparator: '.' };
  return formatVersion(
    numericParts(line.version)!.core,
    (parts?.candidate ?? 0) + 1,
    style,
  );
}

/**
 * The version a new line is cut at: the last release, bumped.
 *
 * Plain `X.Y.Z` whatever the repository's tags look like — it is the input
 * `release-cut` validates and the name of the branch it creates.
 */
export function nextLineVersion(tags: string[], bump: Bump): string | null {
  const releases = tags
    .map(numericParts)
    .filter((parts): parts is NonNullable<typeof parts> => Boolean(parts))
    .filter(parts => parts.candidate === null);
  if (releases.length === 0) {
    return null;
  }
  const last = releases.reduce((best, current) =>
    compareParts(current, best) > 0 ? current : best,
  );
  return bumped(last.core, bump).join('.');
}

/**
 * What promoting a repository does next, under Git Flow.
 *
 *  - `cut`: no line is open, so one is cut from `dev` by dispatching
 *    `release-cut`, which also tags its first candidate.
 *  - `candidate`: the next `-rc.N` on the open line's branch.
 *  - `release`: the line's pull request into `main`. Merging it is what tags
 *    the release — `release-finish` does that, never the portal.
 *
 * Staging takes candidates. Production takes the open line's release. `mgmt`
 * has no staging in front of it, so it both cuts its line and releases it: the
 * candidate it cuts deploys nowhere, it only puts the image in the registry
 * the release is retagged from.
 */
export type ReleaseStep =
  | { kind: 'cut' | 'candidate' | 'release'; to: string }
  | { kind: 'none'; reason: string };

export function nextReleaseStep(
  state: Pick<ReleaseState, 'tags' | 'line'>,
  environment: PromotableEnvironment,
  bump: Bump,
): ReleaseStep {
  const { line, tags } = state;

  if (line && environment === 'staging') {
    return line.unchanged
      ? {
          kind: 'none',
          reason: `${line.branch} has no changes since ${line.candidates[line.candidates.length - 1]} — promote to production instead`,
        }
      : { kind: 'candidate', to: nextCandidate(line) };
  }
  if (line) {
    return line.candidates.length > 0
      ? { kind: 'release', to: line.version }
      : {
          kind: 'none',
          reason: `${line.branch} has no candidate yet, so there is no image to release`,
        };
  }
  if (environment === 'prod') {
    return {
      kind: 'none',
      reason: 'no release line is open — cut one to staging first',
    };
  }

  const version = nextLineVersion(tags, bump);
  return version
    ? { kind: 'cut', to: `${version}-rc.1` }
    : { kind: 'none', reason: 'no earlier release to bump from' };
}

/** Semver order, newest first; tags that are not versions go last. */
export function sortTagsNewestFirst(tags: string[]): string[] {
  return [...tags].sort((a, b) => {
    const left = numericParts(a);
    const right = numericParts(b);
    if (left && right) return compareParts(right, left);
    if (left) return -1;
    if (right) return 1;
    return a.localeCompare(b);
  });
}

/**
 * The release to roll production back to: the highest release below what is
 * running now.
 *
 * An approximation, and labelled as one in the UI. The contract has rollback
 * read the overlay's own git history, because that is what was actually
 * deployed — a tag can exist without ever having shipped. Good enough to show
 * the shape of the screen, not good enough to roll back on.
 */
export function earlierReleases(
  tags: string[],
  currentVersion: string,
): string[] {
  const current = numericParts(currentVersion);
  if (!current) {
    return [];
  }
  const older = tags
    .map(tag => numericParts(tag))
    .filter((parts): parts is NonNullable<typeof parts> => Boolean(parts))
    // Release candidates are excluded on purpose: rolling back to one would
    // put an unreleased build in front of users.
    .filter(parts => parts.candidate === null)
    .filter(parts => compareParts(parts, current) < 0)
    .sort((a, b) => compareParts(b, a));

  const seen = new Set<string>();
  const versions: string[] = [];
  for (const parts of older) {
    const version = `${parts.core[0]}.${parts.core[1]}.${parts.core[2]}`;
    if (!seen.has(version)) {
      seen.add(version);
      versions.push(version);
    }
  }
  return versions;
}


function compareParts(
  a: { core: [number, number, number]; candidate: number | null },
  b: { core: [number, number, number]; candidate: number | null },
): number {
  for (let i = 0; i < 3; i++) {
    if (a.core[i] !== b.core[i]) {
      return a.core[i] - b.core[i];
    }
  }
  if (a.candidate === null && b.candidate === null) return 0;
  if (a.candidate === null) return 1;
  if (b.candidate === null) return -1;
  return a.candidate - b.candidate;
}

function isDeployedUnit(value: unknown): value is DeployedUnit {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const unit = value as Record<string, unknown>;
  return (
    typeof unit.environment === 'string' &&
    typeof unit.component === 'string' &&
    typeof unit.imageTag === 'string'
  );
}

export function isFleetDeploymentsResponse(
  value: unknown,
): value is FleetDeploymentsResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const body = value as Record<string, unknown>;
  if (!Array.isArray(body.services)) {
    return false;
  }
  return body.services.every(entry => {
    if (typeof entry !== 'object' || entry === null) {
      return false;
    }
    const service = entry as Record<string, unknown>;
    return (
      typeof service.service === 'string' &&
      (service.shape === 'standard' || service.shape === 'special') &&
      typeof service.rebuildsPerEnvironment === 'boolean' &&
      Array.isArray(service.units) &&
      service.units.every(isDeployedUnit)
    );
  });
}
